import { useCallback, useEffect, useState } from "react";
import { EonUiPreloaderSquares } from "@eon-ui/eon-ui-components-react";
import { useJwtToken } from "@me8eon/react_authentication";
import { createIncident } from "../../../services/incidentsApi";
import { api } from "../expertApi";
import type { AskContext, BatteryDevice, ItemDetail } from "../aidwops.types";
import useAidwopsStore from "../AidwopsStore";
import useAidwopsTriageStore, { NO_SELECTION } from "../AidwopsTriageStore";
import {
    batteryBand,
    buildSwapTicket,
    formatDays,
    formatRuntime,
} from "./intuneTriage";
import { ChoiceButton, TriageNotice, TriageToolbar } from "./TriageParts";
import "./ExpertQueue.scss";
import LinkedTicketsView from "./LinkedTickets";

type LoadState =
    | { status: "loading" }
    | { status: "error"; message: string }
    | { status: "ready"; devices: BatteryDevice[] };

/** The ServiceNow ticket the engineer asked for: being created, created (with its number), or refused. */
type SnowState =
    | { status: "idle" }
    | { status: "creating" }
    | { status: "created"; number: string; count: number; ignored: number }
    | { status: "error"; message: string };

const SNOW_ASSIGNMENT_GROUP = "infosys-all-mwp-euc-deviceswap";
// Hardcoded while the ticket flow is being integrated; to come from the signed-in user later.
const SNOW_CONTACT_NUMBER = "+49 00000000000";

const keyOf = (device: BatteryDevice, index: number) =>
    device.device_id ?? device.device_name ?? `device-${index}`;

/**
 * The ten devices with the least battery left, for a battery finding. They come from the pre-computed battery
 * index through `GET /api/intune/battery/worst-devices`, enriched with each device's serial number and warranty
 * expiry from the hardware-asset index: no live Intune call, and no user, location or watt-hour figures, because
 * neither index holds them.
 */
export function BatteryDevicesSection({ item }: { item: ItemDetail }) {
    const jwtToken = useJwtToken();
    const submitPrompt = useAidwopsStore((state) => state.submitPrompt);
    const selection =
        useAidwopsTriageStore((state) => state.selections[item.id]) ??
        NO_SELECTION;
    const toggle = useAidwopsTriageStore((state) => state.toggle);
    const setMany = useAidwopsTriageStore((state) => state.setMany);
    const [state, setState] = useState<LoadState>({ status: "loading" });
    const [expanded, setExpanded] = useState<Record<string, boolean>>({});
    const [attempt, setAttempt] = useState(0);
    const [snow, setSnow] = useState<SnowState>({ status: "idle" });
    // Devices staged into a real ServiceNow ticket: removed from the list so staging one looks final, not
    // just a flag toggle. Keyed by `keyOf`, independent of `selection` so clearing/changing other choices
    // afterwards can't bring a staged device back.
    const [stagedKeys, setStagedKeys] = useState<Set<string>>(new Set());

    // A new choice is a new request: the ticket that was created (or refused) belonged to the old one.
    useEffect(() => {
        setSnow((current) =>
            current.status === "creating" ? current : { status: "idle" }
        );
    }, [selection]);

    const getAccessToken = useCallback(async () => {
        const accessToken = await jwtToken();
        if (!accessToken) {
            throw new Error("Unable to acquire access token for expert API");
        }
        return accessToken;
    }, [jwtToken]);

    useEffect(() => {
        let isCurrent = true;
        setState({ status: "loading" });

        void (async () => {
            try {
                const devices = await api.getWorstBatteryDevices(
                    await getAccessToken()
                );
                if (isCurrent) {
                    setState({ status: "ready", devices });
                }
            } catch (error) {
                if (isCurrent) {
                    setState({
                        status: "error",
                        message:
                            error instanceof Error
                                ? error.message
                                : String(error),
                    });
                }
            }
        })();

        return () => {
            isCurrent = false;
        };
        // getAccessToken follows useJwtToken's unstable function identity; a reload is what `attempt` is for.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [attempt]);

    const askAbout = (device: BatteryDevice) => {
        const prompt = device.device_name
            ? `Show battery health for device ${device.device_name}`
            : `Show battery details for Intune device ID ${device.device_id}`;
        const context: AskContext = { view: "queue", selected: item.id };
        void submitPrompt(prompt, getAccessToken, context);
    };

    const label = (
        <div className="expert-queue-item-detail-card-section-label">
            Top 10 worst devices by battery health
        </div>
    );

    if (state.status === "loading") {
        return (
            <div className="expert-queue-item-detail-card-section">
                {label}
                <div className="triage-loading">
                    <EonUiPreloaderSquares />
                    <p className="expert-queue-item-detail-card-section-text">
                        Loading the devices...
                    </p>
                </div>
            </div>
        );
    }

    if (state.status === "error") {
        return (
            <div className="expert-queue-item-detail-card-section">
                {label}
                <div className="ac-alert" role="alert">
                    <p className="ac-alert-title">Couldn't load the devices.</p>
                    <p className="ac-alert-detail">{state.message}</p>
                </div>
                <div>
                    <button
                        type="button"
                        className="triage-button"
                        onClick={() => setAttempt((count) => count + 1)}
                    >
                        Try again
                    </button>
                </div>
            </div>
        );
    }

    const { devices: rawDevices } = state;
    if (rawDevices.length === 0) {
        return (
            <div className="expert-queue-item-detail-card-section">
                {label}
                <p className="expert-queue-item-detail-card-section-text">
                    The battery index lists no device that needs attention.
                </p>
            </div>
        );
    }

    const rawKeys = rawDevices.map(keyOf);
    const visible = rawDevices
        .map((device, index) => ({ device, key: rawKeys[index] }))
        .filter(({ key }) => !stagedKeys.has(key));
    const devices = visible.map((entry) => entry.device);
    const keys = visible.map((entry) => entry.key);

    const swapKeys = keys.filter((key) => selection[key] === "swap");
    const swapDevices = devices.filter(
        (_, index) => selection[keys[index]] === "swap"
    );
    const ignoredDevices = devices.filter(
        (_, index) => selection[keys[index]] === "ignore"
    );
    const swap = swapDevices.length;
    const ignored = ignoredDevices.length;
    const ticket = buildSwapTicket(item.id, swapDevices, ignoredDevices);

    // Same endpoint and payload shape as the ServiceNow incident the assistant raises (services/raiseIncident),
    // but the text is the triage above, not an AI summary of a chat.
    const stageSnowSwap = async () => {
        setSnow({ status: "creating" });
        try {
            const created = await createIncident(
                {
                    contact_number: SNOW_CONTACT_NUMBER,
                    service: "",
                    short_description: ticket.summary,
                    description: ticket.description,
                    external_ticket_id: item.id,
                    category: "User Service Request",
                    sub_category: "Degraded/Service",
                    contact_type: "Self-Service",
                    location_id: "",
                    assignment_group: SNOW_ASSIGNMENT_GROUP,
                },
                jwtToken
            );
            if (!created?.number) {
                throw new Error("ServiceNow answered without a ticket number.");
            }
            setSnow({
                status: "created",
                number: created.number,
                count: swap,
                ignored,
            });
            setStagedKeys((current) => new Set([...current, ...swapKeys]));
        } catch (error) {
            setSnow({
                status: "error",
                message: error instanceof Error ? error.message : String(error),
            });
        }
    };
    const newest = rawDevices
        .map((device) => device.created_at)
        .filter((at): at is string => Boolean(at))
        .sort()
        .pop();

    return (
        <div className="expert-queue-item-detail-card-section">
            {label}
            <p className="expert-queue-item-detail-card-section-text">
                Lowest remaining capacity first, from the battery index
                {newest
                    ? `, record written ${new Date(newest).toLocaleString()}`
                    : ""}
                . It has no user or location.
            </p>

            <TriageToolbar
                counts={[
                    { label: "swap", value: swap },
                    { label: "ignored", value: ignored },
                    {
                        label: "not reviewed",
                        value: keys.length - swap - ignored,
                    },
                ]}
                actions={[
                    {
                        label: "Swap all",
                        onClick: () => setMany(item.id, keys, "swap"),
                    },
                    {
                        label: "Ignore all",
                        onClick: () => setMany(item.id, keys, "ignore"),
                    },
                    {
                        label: "Reset",
                        onClick: () => setMany(item.id, keys, null),
                    },
                ]}
            />

            {devices.length > 0 ? (
                <ul className="triage-list">
                    {devices.map((device, index) => {
                        const key = keys[index];
                        const choice = selection[key];
                        const capacity = device.max_capacity_percentage;
                        const band = batteryBand(capacity);
                        const name =
                            device.device_name ??
                            device.device_id ??
                            "Unknown device";
                        return (
                            <li
                                key={key}
                                className="triage-row"
                                data-choice={choice ?? ""}
                            >
                                <div className="triage-row-top">
                                    <span className="triage-rank">
                                        {index + 1}
                                    </span>
                                    <div className="triage-name">
                                        <strong>{name}</strong>
                                        {device.model && (
                                            <span className="triage-sub">
                                                {device.model}
                                            </span>
                                        )}
                                        {(device.serial_number ||
                                            device.warranty_expiration) && (
                                            <span className="triage-sub">
                                                Serial:{" "}
                                                <strong>
                                                    {device.serial_number ??
                                                        "n/a"}
                                                </strong>
                                                {" · "}
                                                Warranty:{" "}
                                                <strong>
                                                    {device.warranty_expiration
                                                        ? new Date(
                                                              device.warranty_expiration
                                                          ).toLocaleDateString(
                                                              undefined,
                                                              {
                                                                  year: "numeric",
                                                                  month: "short",
                                                                  day: "numeric",
                                                              }
                                                          )
                                                        : "n/a"}
                                                </strong>
                                            </span>
                                        )}
                                    </div>
                                    <span
                                        className={`triage-health triage-health--${band}`}
                                        tabIndex={0}
                                    >
                                        {capacity === null
                                            ? "n/a"
                                            : `${capacity}%`}
                                        {capacity !== null && (
                                            <span className="triage-health-bar">
                                                <i
                                                    style={{
                                                        width: `${Math.min(100, Math.max(0, capacity))}%`,
                                                    }}
                                                />
                                            </span>
                                        )}
                                        <span
                                            className="triage-health-pop"
                                            role="tooltip"
                                        >
                                            <span className="triage-health-pop-head">
                                                <strong>
                                                    {name} ·{" "}
                                                    {capacity === null
                                                        ? "no capacity in the index"
                                                        : `${capacity}% health`}
                                                </strong>
                                                <span
                                                    className={`triage-health-pop-chip triage-health-pop-chip--${band}`}
                                                >
                                                    {band === "critical"
                                                        ? "Critical"
                                                        : band === "degraded"
                                                          ? "Degraded"
                                                          : "Unknown"}
                                                </span>
                                            </span>
                                            <span className="triage-health-pop-grid">
                                                <span>
                                                    <small>Max capacity</small>
                                                    <b>
                                                        {capacity === null
                                                            ? "n/a"
                                                            : `${capacity}% of design`}
                                                    </b>
                                                </span>
                                                <span>
                                                    <small>Est. runtime</small>
                                                    <b>
                                                        {formatRuntime(
                                                            device.estimated_runtime_minutes
                                                        )}
                                                    </b>
                                                </span>
                                                <span>
                                                    <small>Battery age</small>
                                                    <b>
                                                        {formatDays(
                                                            device.battery_age_days
                                                        )}
                                                    </b>
                                                </span>
                                                <span>
                                                    <small>Full drains</small>
                                                    <b>
                                                        {device.full_battery_drain_count ??
                                                            "n/a"}
                                                    </b>
                                                </span>
                                            </span>
                                            <span className="triage-health-pop-foot">
                                                {device.health_status ??
                                                    "No status in the index"}
                                            </span>
                                        </span>
                                    </span>
                                </div>

                                {expanded[key] && (
                                    <dl className="triage-details">
                                        <div>
                                            <dt>Status</dt>
                                            <dd>
                                                {device.health_status ?? "n/a"}
                                            </dd>
                                        </div>
                                        <div>
                                            <dt>Estimated runtime</dt>
                                            <dd>
                                                {formatRuntime(
                                                    device.estimated_runtime_minutes
                                                )}
                                            </dd>
                                        </div>
                                        <div>
                                            <dt>Battery age</dt>
                                            <dd>
                                                {formatDays(
                                                    device.battery_age_days
                                                )}
                                            </dd>
                                        </div>
                                        <div>
                                            <dt>Full drains</dt>
                                            <dd>
                                                {device.full_battery_drain_count ??
                                                    "n/a"}
                                            </dd>
                                        </div>
                                        <div>
                                            <dt>Device ID</dt>
                                            <dd>{device.device_id ?? "n/a"}</dd>
                                        </div>
                                        <div>
                                            <dt>Serial number</dt>
                                            <dd>
                                                {device.serial_number ?? "n/a"}
                                            </dd>
                                        </div>
                                        <div>
                                            <dt>Warranty expiry</dt>
                                            <dd>
                                                {device.warranty_expiration
                                                    ? new Date(
                                                          device.warranty_expiration
                                                      ).toLocaleDateString()
                                                    : "n/a"}
                                            </dd>
                                        </div>
                                        <div>
                                            <dt>Record written</dt>
                                            <dd>
                                                {device.created_at
                                                    ? new Date(
                                                          device.created_at
                                                      ).toLocaleString()
                                                    : "n/a"}
                                            </dd>
                                        </div>
                                    </dl>
                                )}

                                <div className="triage-actions">
                                    <ChoiceButton
                                        label="Swap request"
                                        tone="act"
                                        pressed={choice === "swap"}
                                        onClick={() =>
                                            toggle(item.id, key, "swap")
                                        }
                                    />
                                    <ChoiceButton
                                        label="Ignore"
                                        tone="ignore"
                                        pressed={choice === "ignore"}
                                        onClick={() =>
                                            toggle(item.id, key, "ignore")
                                        }
                                    />
                                    <button
                                        type="button"
                                        className="triage-link"
                                        aria-expanded={Boolean(expanded[key])}
                                        onClick={() =>
                                            setExpanded((current) => ({
                                                ...current,
                                                [key]: !current[key],
                                            }))
                                        }
                                    >
                                        {expanded[key]
                                            ? "Hide details"
                                            : "Details"}
                                    </button>
                                    <button
                                        type="button"
                                        className="triage-link triage-link--end"
                                        disabled={
                                            !device.device_name &&
                                            !device.device_id
                                        }
                                        onClick={() => askAbout(device)}
                                    >
                                        Ask AI
                                    </button>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            ) : (
                <p className="expert-queue-item-detail-card-section-text">
                    All flagged devices have been staged for a swap.
                </p>
            )}

            <div className="triage-snow-queue">
                <div>
                    <strong>
                        {swap} {swap === 1 ? "device" : "devices"} staged for{" "}
                        SNOW Swap Request
                    </strong>
                    <div className="triage-snow-queue-note">
                        {ignored} ignored · creates a ServiceNow ticket
                    </div>
                </div>
                <button
                    type="button"
                    className="triage-snow-button"
                    disabled={
                        swap === 0 ||
                        snow.status === "creating" ||
                        snow.status === "created"
                    }
                    onClick={() => void stageSnowSwap()}
                >
                    {snow.status === "creating" ? (
                        <EonUiPreloaderSquares />
                    ) : (
                        <svg
                            aria-hidden="true"
                            width="14"
                            height="14"
                            viewBox="0 0 16 16"
                            fill="none"
                        >
                            <path
                                d="M3 3.5h10v9H3zM5 6.5h6M5 9.5h3"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    )}
                    Stage SNOW Swap ({swap})
                </button>
            </div>

            <details className="triage-preview">
                <summary>Jira / SNOW note preview</summary>
                <div className="triage-preview-body">
                    <strong>{ticket.summary}</strong>
                    <pre>{ticket.description}</pre>
                </div>
            </details>

            {snow.status === "creating" && (
                <div className="triage-snow-result" role="status">
                    <p>Creating the ServiceNow ticket...</p>
                </div>
            )}
            {snow.status === "created" && (
                <div className="triage-snow-result" role="status">
                    <p>
                        ServiceNow ticket <strong>{snow.number}</strong> created
                        for {snow.count}{" "}
                        {snow.count === 1 ? "device" : "devices"} (
                        {snow.ignored} ignored).
                    </p>
                    <LinkedTicketsView
                        tickets={[
                            {
                                key: snow.number,
                                url: "/incidents",
                                status: snow.status,
                                system: "servicenow",
                            },
                        ]}
                    />
                </div>
            )}
            {snow.status === "error" && (
                <div className="ac-alert" role="alert">
                    <p className="ac-alert-title">
                        Couldn't create the ServiceNow ticket.
                    </p>
                    <p className="ac-alert-detail">{snow.message}</p>
                </div>
            )}

            {/* <TriageNotice what="A swap request marks the device for a hardware swap." /> */}
        </div>
    );
}

export default BatteryDevicesSection;
